import { Loading, Modal, Select } from '@lib/components'
import { Button, ThemeColors } from '@octopus-do/octopus-storybook'
import type { Ga4ReportStatus } from '@store/reducers/ga4'
import { Ga4Actions, ga4Selectors } from '@store/reducers/ga4'
import type React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import type { ModalProps } from '@/types/modal'
import { DisconnectStrip, GaLogo } from './components'
import css from './Ga4PropertyModal.module.scss'
import { buildPropertyOptions, formatUpdatedAt } from './utils'

// Режим окна по статусу на момент открытия. connect — проект без интеграции:
// объяснить, что будет, и увести на консент Google по кнопке. pick_property —
// сразу после консента, интеграция есть, проперти ещё нет. connected — всё
// подключено: показать что, обновить, отключить.
type Mode = 'connect' | 'pick_property' | 'connected'

const modeFor = (status: Ga4ReportStatus): Mode => {
  if (status === 'ok') {
    return 'connected'
  }

  if (status === 'not_connected') {
    return 'connect'
  }

  return 'pick_property'
}

// Единственное окно управления интеграцией, см. Mode.
export const Ga4PropertyModal: React.FC<ModalProps> = ({ onClose }) => {
  const dispatch = useDispatch()

  const status = useSelector(ga4Selectors.getStatus)
  const properties = useSelector(ga4Selectors.getProperties)
  const propertiesLoading = useSelector(ga4Selectors.getPropertiesLoading)
  const saving = useSelector(ga4Selectors.getSavingProperty)
  const resyncing = useSelector(ga4Selectors.getResyncing)
  const connecting = useSelector(ga4Selectors.getConnecting)
  const propertyName = useSelector(ga4Selectors.getPropertyName)
  const hostname = useSelector(ga4Selectors.getHostname)
  const updatedAt = useSelector(ga4Selectors.getUpdatedAt)

  const [selectedProperty, setSelectedProperty] = useState<string | null>(null)
  // Режим окна фиксируем на открытии: упавший отчёт уводит статус из ok, и
  // окно не должно посреди работы превращаться в «выберите проперти».
  const [mode] = useState<Mode>(() => modeFor(status))

  const list = properties || []
  // properties === null — список ещё не запрашивали; пустой массив — аккаунт
  // ответил, и выбирать нечего.
  const loadedEmpty = properties !== null && list.length === 0
  const firstLoad = properties === null && propertiesLoading

  const handleSave = () => {
    const property = list.find((item) => item.propertyId === selectedProperty)

    if (property) {
      dispatch(
        Ga4Actions.selectProperty({
          propertyId: property.propertyId,
          propertyName: property.displayName,
        })
      )
    }
  }

  const handleDisconnect = () => {
    dispatch(Ga4Actions.disconnect())
    onClose()
  }

  if (mode === 'connect') {
    return (
      <Modal
        title="Connect Google Analytics"
        onClose={onClose}
        width={352}
        image={<GaLogo />}
        classnames={{ content: css.modalContent }}
      >
        <div className={ThemeColors.light}>
          <p className={css.intro}>
            Check visits and engagement for every page of the sitemap.
          </p>

          <div className={css.footer}>
            {/* Окно не закрываем: Connect уводит на консент Google целой
                страницей, а если запрос ссылки упал — кнопка отпускается и
                юзер жмёт ещё раз. */}
            <Button
              className={css.wide_button}
              label="Connect"
              loading={connecting}
              onClick={() => dispatch(Ga4Actions.connect())}
            />
            <span className={css.caption}>
              Page metrics for the last 30 days
            </span>
          </div>
        </div>
      </Modal>
    )
  }

  if (mode === 'connected') {
    return (
      <Modal
        title="Google Analytics"
        onClose={onClose}
        width={352}
        image={<GaLogo />}
        classnames={{ content: css.modalContent }}
      >
        <div className={ThemeColors.light}>
          <div className={css.container}>
            {/* Поле как у выбора проперти: показывает подключённое,
                менять здесь нечего. */}
            <div className={css.connected_field}>
              <span className={css.connected_value}>
                {propertyName === null ? hostname : propertyName}
              </span>
            </div>
          </div>

          <div className={css.footer}>
            <Button
              className={css.wide_button}
              priority="secondary"
              label="Resync now"
              loading={resyncing}
              onClick={() => dispatch(Ga4Actions.resync())}
            />
            <span className={css.caption}>
              Synced {formatUpdatedAt(updatedAt)}
            </span>
          </div>

          <DisconnectStrip onClick={handleDisconnect} />
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      title="Connect Google Analytics"
      onClose={onClose}
      width={352}
      image={<GaLogo />}
      classnames={{ content: css.modalContent }}
    >
      <div className={ThemeColors.light}>
        {/* Аккаунту без пропертей объяснение фичи не нужно — на его месте
            говорим, что выбирать нечего. */}
        <p className={css.intro}>
          {loadedEmpty
            ? 'No properties available for this Google account'
            : 'Check visits and engagement for every page of the sitemap.'}
        </p>

        {!loadedEmpty && (
          <div className={css.container}>
            {/* Заглушка ростом в поле выбора: иначе строка ниже прыгает,
                когда список пришёл и на её месте появился input. */}
            {firstLoad && (
              <div className={css.loading_field}>
                <Loading color="gray" type="smallx2" />
              </div>
            )}

            {!firstLoad && (
              // key: Select копирует options в state при монтировании
              // (lib/components/Select: useState(options)), поэтому список,
              // пришедший позже, виден только новому инстансу.
              <Select
                key={list.length}
                type="input"
                inputClassName={css.select_input}
                placeholder="Choose a property"
                selected={selectedProperty}
                disabled={saving}
                options={buildPropertyOptions(list)}
                withSearch
                onChange={(value) => setSelectedProperty(String(value))}
              />
            )}
          </div>
        )}

        <div className={css.footer}>
          {/* Список пуст — на месте Connect повтор запроса: нажимать больше
              нечего, а лоадер ожидания живёт в самой кнопке. */}
          {loadedEmpty ? (
            <Button
              className={css.wide_button}
              label="Reload"
              loading={propertiesLoading}
              onClick={() => dispatch(Ga4Actions.fetchProperties())}
            />
          ) : (
            <Button
              className={css.wide_button}
              label="Connect"
              loading={saving}
              disabled={selectedProperty === null}
              onClick={handleSave}
            />
          )}
          <span className={css.caption}>Page metrics for the last 30 days</span>
        </div>

        {/* Пока проперти выбирают, отключаться не предлагаем. Исключение —
            аккаунт без пропертей: выбрать нечего, и без полосы окно
            становится тупиком. */}
        {loadedEmpty && <DisconnectStrip onClick={handleDisconnect} />}
      </div>
    </Modal>
  )
}
